import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Check,
  Clock,
  Inbox,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  createTodo,
  deleteTodo,
  fetchTodos,
  updateTodo,
} from "../../apis/todos";
import ConfirmationModal from "../../components/ConfirmationModal";
import useAutoRefresh from "../../hooks/useAutoRefresh";
import { displayToast } from "../../utils/Toast";
import "../../styles/pages/tasks/Todos.css";

const FILTER_ALL = "all";
const FILTER_PENDING = "pending";
const FILTER_OVERDUE = "overdue";
const FILTER_COMPLETED = "completed";

const FILTERS = [
  { key: FILTER_ALL, label: "All" },
  { key: FILTER_PENDING, label: "Pending" },
  { key: FILTER_OVERDUE, label: "Overdue" },
  { key: FILTER_COMPLETED, label: "Completed" },
];

const PRIORITIES = [
  { key: "high", label: "High" },
  { key: "medium", label: "Medium" },
  { key: "low", label: "Low" },
];

const EMPTY_MESSAGES = {
  [FILTER_ALL]: "Add something you need to get done.",
  [FILTER_PENDING]: "You have no pending todos.",
  [FILTER_OVERDUE]: "Nothing is overdue.",
  [FILTER_COMPLETED]: "You haven't completed any todos yet.",
};

const emptyForm = () => ({
  title: "",
  description: "",
  priority: "medium",
  date: new Date(),
  time: null,
  allDay: false,
});

const fmtDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const fmtTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const isOverdue = (todo) =>
  Boolean(todo?.scheduled_at) &&
  !todo?.completed &&
  new Date(todo.scheduled_at).getTime() < Date.now();

const buildScheduledAt = (date, time, allDay) => {
  if (!date) return null;
  const dt = new Date(date);
  if (allDay || !time) dt.setHours(0, 0, 0, 0);
  else dt.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return dt.toISOString();
};

const TodosSection = ({ createSignal = 0, focus = null, onSummaryChange }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [search, setSearch] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const submitLockRef = useRef(false);

  const loadTodos = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchTodos();
      if (res?.error) {
        if (!silent) {
          displayToast(
            res?.error?.description || "Failed to load todos",
            "error",
          );
        }
        return;
      }
      setTodos(Array.isArray(res?.items) ? res.items : []);
    } catch (e) {
      if (!silent) displayToast("Failed to load todos", "error");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTodos();
  }, [loadTodos]);

  useAutoRefresh(() => {
    if (showModal) return undefined;
    return loadTodos(true);
  });

  const openAddModal = useCallback(() => {
    setForm(emptyForm());
    setModalMode("add");
    setEditingId(null);
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (createSignal > 0) openAddModal();
  }, [createSignal, openAddModal]);

  useEffect(() => {
    if (!focus?.nonce) return;
    setFilter(FILTER_ALL);
    setSearch(focus.title || "");
  }, [focus]);

  useEffect(() => {
    if (!showModal) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal]);

  const openEditModal = (todo) => {
    const scheduled = todo.scheduled_at ? new Date(todo.scheduled_at) : null;
    setForm({
      title: todo.title || "",
      description: todo.description || "",
      priority: todo.priority || "medium",
      date: scheduled,
      time: todo.is_all_day ? null : scheduled,
      allDay: Boolean(todo.is_all_day),
    });
    setModalMode("edit");
    setEditingId(todo.id);
    setShowModal(true);
  };

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (!form.title.trim()) {
      displayToast("Todo title is required", "error");
      return;
    }
    submitLockRef.current = true;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        priority: form.priority,
        scheduled_at: buildScheduledAt(form.date, form.time, form.allDay),
        is_all_day: form.allDay || !form.time,
      };
      const res =
        modalMode === "edit"
          ? await updateTodo(editingId, payload)
          : await createTodo(payload);
      if (res?.error) {
        displayToast(res?.error?.description || "Failed to save todo", "error");
        return;
      }
      displayToast(
        modalMode === "edit" ? "Todo updated" : "Todo added",
        "success",
      );
      setShowModal(false);
      setForm(emptyForm());
      await loadTodos();
      onSummaryChange?.();
    } catch (err) {
      displayToast("Failed to save todo", "error");
    } finally {
      submitLockRef.current = false;
      setSaving(false);
    }
  };

  const toggleComplete = async (todo) => {
    const next = !todo.completed;
    setTodos((prev) =>
      prev.map((t) => (t.id === todo.id ? { ...t, completed: next } : t)),
    );
    try {
      const res = await updateTodo(todo.id, {
        title: todo.title,
        description: todo.description || "",
        priority: todo.priority || "medium",
        scheduled_at: todo.scheduled_at || null,
        is_all_day: Boolean(todo.is_all_day),
        completed: next,
      });
      if (res?.error) throw new Error(res.error.description);
      onSummaryChange?.();
    } catch (err) {
      setTodos((prev) =>
        prev.map((t) => (t.id === todo.id ? { ...t, completed: !next } : t)),
      );
      displayToast("Failed to update todo", "error");
    }
  };

  const confirmDelete = async () => {
    const todo = pendingDelete;
    setPendingDelete(null);
    if (!todo) return;
    try {
      const res = await deleteTodo(todo.id);
      if (res?.error) {
        displayToast(
          res?.error?.description || "Failed to delete todo",
          "error",
        );
        return;
      }
      displayToast("Todo deleted", "success");
      await loadTodos();
      onSummaryChange?.();
    } catch (err) {
      displayToast("Failed to delete todo", "error");
    }
  };

  const visibleTodos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return todos
      .filter((t) => {
        if (filter === FILTER_PENDING) return !t.completed;
        if (filter === FILTER_COMPLETED) return Boolean(t.completed);
        if (filter === FILTER_OVERDUE) return isOverdue(t);
        return true;
      })
      .filter(
        (t) =>
          !q ||
          (t.title || "").toLowerCase().includes(q) ||
          (t.description || "").toLowerCase().includes(q),
      )
      .sort((a, b) => {
        if (Boolean(a.completed) !== Boolean(b.completed)) {
          return a.completed ? 1 : -1;
        }
        const at = a.scheduled_at
          ? new Date(a.scheduled_at).getTime()
          : Infinity;
        const bt = b.scheduled_at
          ? new Date(b.scheduled_at).getTime()
          : Infinity;
        return at - bt;
      });
  }, [todos, filter, search]);

  const isTrulyEmpty = todos.length === 0 && !search.trim();

  return (
    <section className="todos-board">
      <div className="todos-head">
        <div>
          <h2 className="todos-title">My Todos</h2>
          <p className="todos-sub">
            Private checklist — only you can see these
          </p>
        </div>
        <div className="todos-head-actions">
          <div className="tasks-search">
            <Search size={15} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search todos…"
            />
            {search && (
              <button
                type="button"
                className="tasks-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            className="tasks-add-btn"
            onClick={openAddModal}
          >
            <Plus size={18} />
            <span>Add Todo</span>
          </button>
        </div>
      </div>

      <div className="tasks-status-seg todos-seg">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`tasks-seg-btn ${filter === f.key ? "active" : ""}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="tasks-state">
          <Loader2 className="tasks-spin" size={30} />
          <p>Loading todos…</p>
        </div>
      ) : visibleTodos.length === 0 ? (
        <div className="tasks-state">
          <div className="tasks-empty-icon">
            <Inbox size={34} />
          </div>
          <h3>No todos here</h3>
          <p>{EMPTY_MESSAGES[filter]}</p>
          {isTrulyEmpty && (
            <button className="tasks-add-btn ghost" onClick={openAddModal}>
              <Plus size={18} />
              <span>Add Todo</span>
            </button>
          )}
        </div>
      ) : (
        <ul className="todos-list">
          {visibleTodos.map((todo) => {
            const overdue = isOverdue(todo);
            return (
              <li
                key={todo.id}
                className={`todo-card ${todo.completed ? "done" : ""} ${
                  overdue ? "overdue" : ""
                }`}
              >
                <button
                  type="button"
                  className={`task-check ${todo.completed ? "checked" : ""}`}
                  onClick={() => toggleComplete(todo)}
                  title={todo.completed ? "Mark as pending" : "Mark as done"}
                  aria-label={
                    todo.completed ? "Mark as pending" : "Mark as done"
                  }
                >
                  {todo.completed && <Check size={14} />}
                </button>

                <div className="todo-body">
                  <div className="todo-top">
                    <h3 className="todo-title">{todo.title}</h3>
                    <span
                      className={`todo-priority ${todo.priority || "medium"}`}
                    >
                      {todo.priority || "medium"}
                    </span>
                  </div>
                  {todo.description && (
                    <p className="todo-desc">{todo.description}</p>
                  )}
                  {todo.scheduled_at && (
                    <span className={`todo-meta ${overdue ? "alert" : ""}`}>
                      <Clock size={13} />
                      {fmtDate(todo.scheduled_at)}
                      {todo.is_all_day
                        ? " · All day"
                        : ` · ${fmtTime(todo.scheduled_at)}`}
                      {overdue ? " · Overdue" : ""}
                    </span>
                  )}
                </div>

                <div className="task-actions">
                  <button
                    type="button"
                    className="task-action edit"
                    onClick={() => openEditModal(todo)}
                    aria-label="Edit todo"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    className="task-action delete"
                    onClick={() => setPendingDelete(todo)}
                    aria-label="Delete todo"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showModal && (
        <div
          className="task-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="task-modal" role="dialog" aria-modal="true">
            <div className="task-modal-head">
              <h2>{modalMode === "edit" ? "Edit Todo" : "Add My Todo"}</h2>
              <button
                className="task-modal-close"
                onClick={() => setShowModal(false)}
                disabled={saving}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            <form className="task-form" onSubmit={handleSubmit}>
              <label className="task-field">
                <span>
                  Title <b>*</b>
                </span>
                <input
                  type="text"
                  value={form.title}
                  onChange={setField("title")}
                  placeholder="What do you need to do?"
                  maxLength={255}
                  autoFocus
                />
              </label>

              <label className="task-field">
                <span>Description</span>
                <textarea
                  value={form.description}
                  onChange={setField("description")}
                  placeholder="Add more details (optional)"
                  rows={3}
                  maxLength={5000}
                />
              </label>

              <div className="task-field">
                <span>Priority</span>
                <div className="todo-priority-seg">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      className={`todo-priority-btn ${p.key} ${
                        form.priority === p.key ? "active" : ""
                      }`}
                      onClick={() =>
                        setForm((prev) => ({ ...prev, priority: p.key }))
                      }
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="task-field-row">
                <div className="task-field">
                  <span>Date</span>
                  <DatePicker
                    selected={form.date}
                    onChange={(d) => setForm((prev) => ({ ...prev, date: d }))}
                    dateFormat="dd-MM-yyyy"
                    className="task-datepicker-input"
                    wrapperClassName="task-datepicker-wrapper"
                    popperProps={{ strategy: "fixed" }}
                    portalId="root-portal"
                    placeholderText="Select date"
                  />
                </div>
                <div className="task-field">
                  <span>
                    Time <em>(optional)</em>
                  </span>
                  <DatePicker
                    selected={form.time}
                    onChange={(t) =>
                      setForm((prev) => ({ ...prev, time: t, allDay: !t }))
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="task-datepicker-input"
                    wrapperClassName="task-datepicker-wrapper"
                    popperProps={{ strategy: "fixed" }}
                    portalId="root-portal"
                    placeholderText="Select time"
                    isClearable
                    disabled={form.allDay}
                  />
                </div>
              </div>

              <label className="todo-allday">
                <input
                  type="checkbox"
                  checked={form.allDay}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      allDay: e.target.checked,
                      time: e.target.checked ? null : prev.time,
                    }))
                  }
                />
                <span>All day</span>
              </label>

              <div className="task-form-actions">
                <button
                  type="button"
                  className="task-btn secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="task-btn primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="tasks-spin" size={16} /> Saving…
                    </>
                  ) : (
                    <>
                      <Check size={16} />{" "}
                      {modalMode === "edit" ? "Save Changes" : "Add Todo"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onCancel={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        message={`Delete "${pendingDelete?.title || ""}"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </section>
  );
};

export default TodosSection;
